/**
 * Object — freestanding placeable fixtures/appliances (OBJECTS round, 2026-08 —
 * docs/MODELER.md "OBJECT ANATOMY"). Two representations, the ArchiCAD GDL split:
 * an AUTHORED parametric PLAN SYMBOL (bim-geometry object/planSymbols.ts — a drafting
 * convention, never a mesh projection or a cut) and a 3D BODY (object/generate3d.ts —
 * v1 procedural prototypes, instanced per TYPE like doors/windows/casework).
 *
 * Position = a single-node `path` param (the anchor at the object's BACK-CENTER,
 * objectKinds.ts frame convention) so the shared machinery works unchanged:
 * translateElement probes `path`, duplicateElement refreshes its node id, the
 * pet-palette Move arms the standard two-click translate, and kind 'path2d' keeps
 * it out of eyedropper transfer automatically. This positional independence from
 * any host is UNCHANGED by the round below — hosting only ever derives Z, never
 * (x, y, rotation).
 *
 * SURFACE HOSTING round (2026-08-24, Matt: "casework, but also other surfaces
 * like slabs and other objects too, like if we had a pre manufactured vanity"):
 * REVERSES the original "freestanding by design, no host" stance recorded here
 * (a faucet/sink's real height is the countertop/vanity/floor underneath it, not
 * a flat number guessed off the level — move the counter, the old flat number
 * doesn't follow). `hostable.hostsInto` below lists the three surface kinds
 * worth deriving Z from; height-blind categories (`heightOffsetFt`'s own
 * meaning) still apply exactly as before when unhosted — hosting is an
 * ENHANCEMENT auto-detected at placement (`surfaceHostAtPoint`, snap.ts), never
 * a requirement, unlike ceiling-fixture hosting. Deliberately excludes
 * 'ceiling' (objects rest on things via gravity — a ceiling's own top face
 * is the underside of the floor ABOVE, not a surface this room's objects sit
 * on) and 'roof' (a sloped surface needs per-point evaluation, not a flat
 * top-Z lookup — real but narrower need, e.g. rooftop equipment; deferred
 * until asked for).
 */
import type { CategorySchema } from '../../registry'
import {
  OBJECT_KINDS, OBJECT_KINDS_WITH_RIM, OBJECT_KINDS_WALL_MOUNT, FURNITURE_SIZED_KINDS, BED_SIZES,
  RANGE_COOKTOP_EXTRAS, HOOD_BOTTOM_DEFAULT_FT, COOKTOP_STYLES, FIRE_PIT_MEDIA, BIN_STREAMS, FABRIC_COLORS, CHAISE_FRAMES, CHAISE_BACKS,
  BED_FRAME_MARGIN_IN, BED_HEADBOARD_IN,
} from './objectKinds'
import { OBJECT_FAMILY_CHOICES } from '../../families'
import { FUEL_KINDS } from '../../hookups'
import { PLANT_SPECIES } from './plantSpecies'
import { APPLIANCE_SIZES, APPLIANCE_SIZED_KINDS, applianceSizeChoices } from './applianceSizes'

import {
  PLUMBING_STYLES, STYLED_PLUMBING_KINDS, SIZED_PLUMBING_KINDS, PLUMBING_SIZE_CHOICES, BODY_FINISHES,
  plumbingStyleChoices,
} from './plumbingStyles'
import { WALL_HUNG } from './wallHungToilet'
import type { ParamDef } from '../../params'

/** PLUMBING STYLES (2026-10-07): one Style choice per styled kind (its own list), the Size it
 *  comes in, its Body Finish — all per INSTANCE (the type is the generic fixture). */
const PLUMBING_STYLE_PARAMS: ParamDef[] = STYLED_PLUMBING_KINDS.map((kind) => ({
  key: PLUMBING_STYLES[kind].param, label: 'Style', kind: 'choice' as const,
  default: PLUMBING_STYLES[kind].styles[0].value,
  choices: plumbingStyleChoices(kind),
  conditionalOn: { param: 'objectKind', equals: kind },
}))
const BODIED_PLUMBING_KINDS = STYLED_PLUMBING_KINDS.filter((k) => PLUMBING_STYLES[k].styles.some((st) => st.body))
const UNSTYLED_OBJECT_KINDS = OBJECT_KINDS.map((k) => k.value as string).filter((k) => !STYLED_PLUMBING_KINDS.includes(k))

export const objectSchema: CategorySchema = {
  id: 'object',
  // objects sit ON slabs and against walls — they win overlapping 3D picks like
  // openings do (plan pick priority lives in the app's pickConfigs entry)
  pickRank: 0,
  // PLANT HOSTING (2026-10-05): terrain joins the list — a plant sits on the graded
  // ground (its height read off the terrain at its center), the fence/railing precedent
  hostable: { hostsInto: ['casework', 'floor', 'object', 'topo'] },
  label: 'Object',
  typeParams: {
    groups: [
      {
        label: 'Object',
        params: [
          {
            key: 'objectKind', label: 'Kind', kind: 'choice', default: 'toilet',
            choices: OBJECT_KINDS.map((k) => ({ value: k.value, label: k.label })),
          },
          {
            // PLANTING: which plant (a TYPE param — one type per species) — drives the plan symbol, the 3D stand-in, and the
            // stylized swaying plant the viewer grows from the same id
            key: 'plantSpecies', label: 'Species', kind: 'choice', default: 'olive',
            choices: PLANT_SPECIES.map((s) => ({ value: s.id, label: `${s.name} (${s.botanical})` })),
            conditionalOn: { param: 'objectKind', equals: 'plant' },
          },
          // FAMILY CATEGORIES round: '' = Auto (derive from Kind — see
          // families.ts#familyCategoryOf) — the common case; only set this to
          // move a specific type into a different bucket than its kind's default
          // (e.g. reclassifying a captured mesh out of Furniture).
          {
            key: 'familyCategory', label: 'Family', kind: 'choice', default: '',
            choices: OBJECT_FAMILY_CHOICES.map((v) => ({ value: v, label: v || 'Auto (from Kind)' })),
          },
          // PLUMBING STYLES: a styled fixture is the size of its style — no type size fields
          { key: 'widthFt', label: 'Width', kind: 'length', default: 2, min: 0.1, max: 20, conditionalOn: { param: 'objectKind', equals: UNSTYLED_OBJECT_KINDS } },
          // FIT-OUT CATALOG: thin items (shower glass, registers) need small minimums
          { key: 'depthFt', label: 'Depth', kind: 'length', default: 2, min: 0.02, max: 20, conditionalOn: { param: 'objectKind', equals: UNSTYLED_OBJECT_KINDS } },
          { key: 'heightFt', label: 'Height', kind: 'length', default: 2.5, min: 0.02, max: 8, conditionalOn: { param: 'objectKind', equals: UNSTYLED_OBJECT_KINDS } },
        ],
      },
      {
        label: 'Fixture',
        params: [
          {
            key: 'basins', label: 'Basins', kind: 'number', default: 1, min: 1, max: 2,
            conditionalOn: { param: 'objectKind', equals: 'kitchenSink' },
          },
          {
            // the rim/deck elevation for counter-mounted kinds — the prototype's body
            // hangs from this height (a sink usually reads over casework; the object
            // itself stays freestanding, per the no-auto-coupling doctrine)
            key: 'rimHeightFt', label: 'Rim Height', kind: 'length', default: 3, min: 1, max: 4,
            conditionalOn: { param: 'objectKind', equals: [...OBJECT_KINDS_WITH_RIM] },
          },
          {
            // KITCHEN APPLIANCES round: a range's cooktop — burner count plus an
            // optional griddle and/or charbroiler (the Wolf configurations)
            key: 'burners', label: 'Burners', kind: 'number', default: 4, min: 2, max: 10,
            conditionalOn: { param: 'objectKind', equals: 'range' },
          },
          {
            key: 'cooktopExtra', label: 'Cooktop', kind: 'choice', default: 'none',
            choices: RANGE_COOKTOP_EXTRAS.map((c) => ({ value: c.value, label: c.label })),
            conditionalOn: { param: 'objectKind', equals: 'range' },
          },
          {
            // SHOWER TRIM (2026-09-30): the valve's CENTER / the showerhead's arm,
            // above the object's base (the floor it sits over). Blank = the kind's
            // firm default (objectKinds.ts#WALL_MOUNT_DEFAULT_FT: 4'-0" / 6'-8").
            key: 'mountHeightFt', label: 'Mount Height', kind: 'length', default: 0, min: 0, max: 10,
            conditionalOn: { param: 'objectKind', equals: [...OBJECT_KINDS_WALL_MOUNT] },
          },
          {
            // DERIVED HOOKUPS round: what the appliance burns — sets its hookups
            // (gas + 120V, or a 240V circuit). Blank = the kind's usual fuel
            // (hookups.ts#FUEL_KINDS).
            key: 'fuel', label: 'Fuel', kind: 'choice', default: '',
            choices: [{ value: '', label: 'Standard for this appliance' }, { value: 'gas', label: 'Gas' }, { value: 'electric', label: 'Electric' }],
            conditionalOn: { param: 'objectKind', equals: Object.keys(FUEL_KINDS) },
          },
        ],
      },
      {
        label: 'Appearance',
        params: [
          { key: 'surfaceMaterial', label: 'Material', kind: 'material', default: '' },
        ],
      },
      {
        label: '3D Body',
        params: [
          // PHOTO-TO-3D round (docs/bim-plan/17): the reserved slot, finally consumed —
          // the firm-library Storage asset id (`lib/<assetId>`) of a sanitized photo/
          // manufacturer mesh. Being a TYPE param it forks the prototype through
          // protoKeyFor automatically. readonly: curators attach meshes through the
          // capture → review → bake flow, never by typing a ref here.
          { key: 'meshRef', label: 'Mesh (Firm Library)', kind: 'text', default: '', readonly: true },
          {
            // PHOTO-TO-3D round: the BAKED top-down plan silhouette — PlanSymbolPath[]
            // JSON in the object local frame (anchor back-center, +y front), authored
            // ONCE at bake time. Riding the type record as a param means it copies
            // into models through syncFirmLibrary for free, needs no async fetch on
            // the plan path, and keeps the "parameters only, never geometry" doctrine
            // intact (a baked 2D symbol is drafting data, the same class as path2d —
            // the 3D mesh is the geometry and it stays in Storage). `hidden`: real
            // resolved data, but never a form field — the bake owns it.
            key: 'planSymbol', label: 'Baked Plan Symbol', kind: 'data', default: null, hidden: true,
          },
        ],
      },
    ],
  },
  instanceParams: {
    groups: [
      {
        label: 'Style',
        params: [
          ...PLUMBING_STYLE_PARAMS,
          {
            // the width this one comes in (a tub's length) — a size the style doesn't offer
            // falls back to the style's own
            key: 'fixtureSizeIn', label: 'Size', kind: 'choice', default: '',
            choices: [{ value: '', label: 'Style standard' }, ...PLUMBING_SIZE_CHOICES],
            conditionalOn: { param: 'objectKind', equals: [...SIZED_PLUMBING_KINDS] },
          },
          {
            // WALL-HUNG TOILET (2026-10-21): the top of the ceramic above the floor — the
            // carrier's feet set it (15-3/4" standard; 16-1/4" to 18-1/4" puts an ADA seat at
            // 17" to 19"); the rods, waste, tank and flush plate all move with it
            key: 'wallHungRimFt', label: 'Rim Height', kind: 'length', default: WALL_HUNG.rimStd / 12,
            min: WALL_HUNG.rimMin / 12, max: WALL_HUNG.rimMax / 12,
            conditionalOn: { param: 'objectKind', equals: 'wallHungToilet' },
          },
          {
            key: 'bodyFinish', label: 'Body Finish', kind: 'choice', default: '',
            choices: [{ value: '', label: 'Style standard' }, ...BODY_FINISHES.map((b) => ({ value: b.value, label: b.label }))],
            conditionalOn: { param: 'objectKind', equals: BODIED_PLUMBING_KINDS },
          },
          {
            // the Paint Brush's color on the fixture's metal (null = the style's own finish,
            // else the project standard) — brushed, never typed
            key: 'paintColor', label: 'Color', kind: 'data', default: null, hidden: true,
            conditionalOn: { param: 'objectKind', equals: [...STYLED_PLUMBING_KINDS] },
          },
        ],
      },
      {
        label: 'Placement',
        params: [
          // single-node anchor path — see the header note; kind 'path2d' is skipped by
          // eyedropper transfer and rendered read-only by the palette
          { key: 'path', label: 'Anchor', kind: 'path2d', default: null },
          // DERIVED HOOKUPS round: the hookups the user turned off on this object
          // (keys from hookups.ts#objectHookups) — everything else derives
          { key: 'hookupsOff', label: 'Hookups Off', kind: 'data', default: null, hidden: true },
          // CASEWORK STUDIO stage 4: the box a Studio-placed fixture belongs to, and its role
          { key: 'studioBoxId', label: 'Studio Box', kind: 'data', default: null, hidden: true },
          { key: 'studioRole', label: 'Studio Role', kind: 'data', default: null, hidden: true },
          { key: 'studioPlace', label: 'Studio Place', kind: 'data', default: null, hidden: true },
          // CASEWORK STUDIO stage B: a built-in appliance's unit (its look) and whether it shows
          // its own grille at the floor (an undercounter refrigerator with no toe kick in front)
          { key: 'applianceUnit', label: 'Appliance', kind: 'data', default: null, hidden: true },
          { key: 'applianceKind', label: 'Appliance Kind', kind: 'data', default: null, hidden: true },
          { key: 'applianceGrille', label: 'Appliance Grille', kind: 'data', default: null, hidden: true },
          {
            // 0 faces world +y; the rotate grip and placement aim write this
            key: 'rotationDeg', label: 'Rotation', kind: 'number', default: 0,
            min: -360, max: 360, positional: true,
          },
          {
            // mounting height off the level (wall-hung fixtures, counters the object
            // rides over) — copying it onto another object would MOVE it: positional
            key: 'heightOffsetFt', label: 'Height Offset', kind: 'length', default: 0,
            min: -5, max: 12, positional: true,
            // the hood insert carries its own Height Above Level instead
            conditionalOn: { param: 'objectKind', equals: OBJECT_KINDS.filter((k) => k.value !== 'hoodInsert').map((k) => k.value) },
          },
          {
            // KITCHEN APPLIANCES round (Matt: "allow the user to set its height above the
            // level as an instance parameter … a default common set height"): the hood's
            // BOTTOM above the level — 5'-6" default (30" over a 36" cooktop)
            key: 'hoodBottomFt', label: 'Height Above Level', kind: 'length', default: HOOD_BOTTOM_DEFAULT_FT,
            min: 3, max: 10,
            conditionalOn: { param: 'objectKind', equals: 'hoodInsert' },
          },
        ],
      },
      {
        // FIREPLACE FLUES (2026-10-04, Matt — docs/FIREPLACE_FLUES.md): the vent up
        // through the roof, its 3-2-10 termination, the spark arrestor, and an optional
        // decorative shroud sized to the chase walls. All derived from the roof and
        // walls around it (bim-geometry object/flue.ts); these are the choices.
        label: 'Flue & Chimney',
        params: [
          {
            key: 'ventType', label: 'Venting', kind: 'choice', default: 'auto',
            choices: [
              { value: 'auto', label: 'Standard for this unit' },
              { value: 'direct', label: 'Direct vent (no roof flue)' },
              { value: 'bvent', label: 'B-vent (natural draft gas)' },
              { value: 'classA', label: 'Class A chimney pipe (wood)' },
            ],
            conditionalOn: { param: 'objectKind', equals: ['fireplaceLinear', 'fireplaceWood', 'woodStove'] },
          },
          {
            // inside diameter, inches; 0 = the vent type's usual size (B-vent 6", Class A 8")
            key: 'flueDiameterIn', label: 'Flue Size (in)', kind: 'number', default: 0, min: 0, max: 16,
            conditionalOn: { param: 'objectKind', equals: ['fireplaceLinear', 'fireplaceWood', 'woodStove'] },
          },
          {
            key: 'shroud', label: 'Chimney Shroud', kind: 'choice', default: 'none',
            choices: [
              { value: 'none', label: 'None' },
              { value: 'hip', label: 'Hip / pyramid cap' },
              { value: 'box', label: 'Flat-top box' },
              { value: 'barrel', label: 'Arched / barrel' },
              { value: 'pagoda', label: 'Two-tier pagoda' },
            ],
            conditionalOn: { param: 'objectKind', equals: ['fireplaceLinear', 'fireplaceWood', 'woodStove'] },
          },
          {
            key: 'shroudMaterial', label: 'Shroud Material', kind: 'material', default: '',
            conditionalOn: { param: 'objectKind', equals: ['fireplaceLinear', 'fireplaceWood', 'woodStove'] },
          },
          {
            // "If there is not a completed chase and chimney, it should assume we want an
            // exposed flue … but should give us a warning to clear": ticking this clears it
            key: 'exposedFlueOk', label: 'Exposed flue confirmed', kind: 'bool', default: false,
            conditionalOn: { param: 'objectKind', equals: ['fireplaceLinear', 'fireplaceWood', 'woodStove'] },
          },
        ],
      },
      {
        // SHOWER PAN SIZE (2026-09-30, Matt: "the shower pan size to be an instance
        // parameter … so I can adjust it to fit in a multitude of openings"): each
        // placed pan carries its OWN width/depth; 0 = the type's size. doc.ts's
        // resolveElement folds a set value into the resolved typeParams, so every
        // consumer (3D prototype + its instancing key, plan symbol, pick, snap)
        // sees the real size with no change of its own.
        // FURNITURE (2026-09-30, Matt: "parametric by instance parameter with a default
        // setting but allowing us to change their sizes"): 0 = the type's size; doc.ts
        // folds a set value into the resolved typeParams (the shower-pan mechanism)
        label: 'Size',
        params: [
          {
            // STANDARD APPLIANCE SIZES (2026-10-15): the kind's typical sizes as one pick —
            // choosing one writes its width / depth / height below (elements.ts#applyParamEdit);
            // typing a dimension makes it Custom
            key: 'applianceSize', label: 'Standard Size', kind: 'choice', default: '',
            choices: [{ value: '', label: 'Custom' }, ...new Map(APPLIANCE_SIZED_KINDS.flatMap((k) => APPLIANCE_SIZES[k].map((s) => [s.value, { value: s.value, label: s.label }] as const))).values()],

            choicesBy: { param: 'objectKind', map: Object.fromEntries(APPLIANCE_SIZED_KINDS.map((k) => [k, applianceSizeChoices(k)])) },
            conditionalOn: { param: 'objectKind', equals: [...APPLIANCE_SIZED_KINDS] },
          },
          {
            key: 'sizeWidthFt', label: 'Width', kind: 'length', default: 0, min: 0, max: 20,
            conditionalOn: { param: 'objectKind', equals: [...FURNITURE_SIZED_KINDS] },
          },
          {
            // an octagonal umbrella's Width is its diameter — no separate depth
            key: 'sizeDepthFt', label: 'Depth', kind: 'length', default: 0, min: 0, max: 20,
            conditionalOn: { param: 'objectKind', equals: FURNITURE_SIZED_KINDS.filter((k) => k !== 'umbrellaOct') },
          },
          {
            key: 'sizeHeightFt', label: 'Height', kind: 'length', default: 0, min: 0, max: 10,
            conditionalOn: { param: 'objectKind', equals: [...FURNITURE_SIZED_KINDS, 'bed'] },
          },
          {
            // LIVING + GAME ROOM round: the dashed room-to-play zone around a table
            key: 'playClearance', label: 'Show Play Clearance', kind: 'bool', default: false,
            conditionalOn: { param: 'objectKind', equals: ['poolTable', 'pingPongTable'] },
          },
          {
            // the L's return goes LEFT or RIGHT (facing the sofa)
            key: 'sectionalHand', label: 'Return Side', kind: 'choice', default: 'left',
            choices: [{ value: 'left', label: 'Left' }, { value: 'right', label: 'Right' }],
            conditionalOn: { param: 'objectKind', equals: 'sectional' },
          },
          {
            // PARKING STALLS (2026-10-01, Matt): which of the jurisdiction's required stall
            // sizes this car shows as a dashed line on plans (centered on the car). The SIZES
            // come from the zoning research (database/281 via the project's parcel), never
            // from here — only the stall TYPE is the car's own setting.
            // 'auto' (2026-10-20, Matt: "sense when a parking space is covered"): an enclosed
            // garage around the car → the garage stall, else the standard one — read from the
            // model (frontend annotation/parkingStalls.js), never stored
            key: 'parkingStall', label: 'Parking Stall', kind: 'choice', default: 'auto',
            choices: [
              { value: 'auto', label: 'Automatic (garage when walled in)' },
              { value: 'standard', label: 'Standard' },
              { value: 'compact', label: 'Compact' },
              { value: 'garage', label: 'Enclosed garage' },
              { value: 'accessible', label: 'Accessible (ADA)' },
              { value: 'none', label: 'None' },
            ],
            conditionalOn: { param: 'objectKind', equals: ['carSedan', 'carSUV'] },
          },
          // DERIVED HOOKUPS round: a bidet seat adds a GFCI receptacle beside the toilet
          {
            key: 'bidetSeat', label: 'Bidet Seat', kind: 'bool', default: false,
            conditionalOn: { param: 'objectKind', equals: ['toilet', 'wallHungToilet'] },
          },
          // DINING CHAIRS BY COUNT (2026-10-06): the table sets this many real chairs
          // around itself (commands/diningChairs.ts — applyParamEdit routes the write)
          {
            key: 'chairCount', label: 'Chairs', kind: 'number', default: 0, min: 0, max: 16,
            conditionalOn: { param: 'objectKind', equals: 'diningTable' },
          },
          // FREESTANDING TUB (2026-10-06): the floor-mounted filler on/off (off = a wall
          // or deck-mounted filler — its supply then lands on the wall behind)
          {
            key: 'tubFiller', label: 'Floor-Mounted Filler', kind: 'bool', default: true,
            conditionalOn: { param: 'objectKind', equals: 'tubFreestanding' },
          },
          // SITE FURNISHINGS (2026-10-06): the trampoline's safety enclosure on/off
          {
            key: 'trampolineNet', label: 'Safety Net', kind: 'bool', default: true,
            conditionalOn: { param: 'objectKind', equals: ['trampoline', 'trampolineRect'] },
          },
          // FIRE PIT (2026-10-19): sized per placement — 0 = the type's size
          {
            key: 'pitLengthFt', label: 'Length', kind: 'length', default: 0, min: 0, max: 30,
            conditionalOn: { param: 'objectKind', equals: 'firePit' },
          },
          {
            key: 'pitWidthFt', label: 'Width', kind: 'length', default: 0, min: 0, max: 30,
            conditionalOn: { param: 'objectKind', equals: 'firePit' },
          },
          {
            key: 'pitHeightFt', label: 'Height (top of cap)', kind: 'length', default: 0, min: 0, max: 4,
            conditionalOn: { param: 'objectKind', equals: 'firePit' },
          },
          {
            key: 'pitMedia', label: 'Fire Media', kind: 'choice', default: '',
            choices: [{ value: '', label: 'Fire balls (standard)' }, ...FIRE_PIT_MEDIA.filter((m) => m.value !== 'balls').map((m) => ({ value: m.value, label: m.label }))],
            conditionalOn: { param: 'objectKind', equals: 'firePit' },
          },
          {
            // blank = a neutral stone colour; pick the house's stone to match it
            key: 'pitStoneMaterial', label: 'Stone', kind: 'material', default: '',
            conditionalOn: { param: 'objectKind', equals: 'firePit' },
          },
          {
            // blank = buff polished concrete
            key: 'pitCapMaterial', label: 'Cap', kind: 'material', default: '',
            conditionalOn: { param: 'objectKind', equals: 'firePit' },
          },
          // BUBBLER FOUNTAIN (2026-10-19): sized per placement - 0 = the type's size
          {
            key: 'fountainDiameterFt', label: 'Diameter', kind: 'length', default: 0, min: 0, max: 20,
            conditionalOn: { param: 'objectKind', equals: 'fountain' },
          },
          {
            key: 'fountainHeightFt', label: 'Height', kind: 'length', default: 0, min: 0, max: 6,
            conditionalOn: { param: 'objectKind', equals: 'fountain' },
          },
          {
            // blank = a limestone finish
            key: 'fountainMaterial', label: 'Finish', kind: 'material', default: '',
            conditionalOn: { param: 'objectKind', equals: 'fountain' },
          },
          // YARD PIECES (2026-10-19): each piece's colours per placement (blank = the usual)
          {
            key: 'binStream', label: 'Cart', kind: 'choice', default: '',
            choices: [{ value: '', label: 'Trash (black)' }, ...BIN_STREAMS.filter((c) => c.value !== 'trash').map((c) => ({ value: c.value, label: c.label }))],
            conditionalOn: { param: 'objectKind', equals: 'trashCart' },
          },
          {
            key: 'canopyColor', label: 'Canopy', kind: 'choice', default: '',
            choices: [{ value: '', label: 'Natural' }, ...FABRIC_COLORS.filter((c) => c.value !== 'natural').map((c) => ({ value: c.value, label: c.label }))],
            conditionalOn: { param: 'objectKind', equals: ['umbrellaOct', 'umbrellaRect'] },
          },
          {
            key: 'chaiseBack', label: 'Back', kind: 'choice', default: '',
            choices: [{ value: '', label: 'Reclined' }, ...CHAISE_BACKS.filter((c) => c.value !== 'reclined').map((c) => ({ value: c.value, label: c.label }))],
            conditionalOn: { param: 'objectKind', equals: 'chaise' },
          },
          {
            key: 'cushionColor', label: 'Cushion', kind: 'choice', default: '',
            choices: [{ value: '', label: 'White' }, ...FABRIC_COLORS.filter((c) => c.value !== 'white').map((c) => ({ value: c.value, label: c.label }))],
            conditionalOn: { param: 'objectKind', equals: 'chaise' },
          },
          {
            key: 'chaiseFrame', label: 'Frame', kind: 'choice', default: '',
            choices: [{ value: '', label: 'Teak' }, ...CHAISE_FRAMES.filter((c) => c.value !== 'teak').map((c) => ({ value: c.value, label: c.label }))],
            conditionalOn: { param: 'objectKind', equals: 'chaise' },
          },
          // SITE UTILITIES (2026-10-07): an earthquake (seismic) gas shutoff on the house
          // line above the meter — per placement (some jurisdictions require one)
          {
            key: 'gasSeismicValve', label: 'Earthquake Shutoff Valve', kind: 'bool', default: false,
            conditionalOn: { param: 'objectKind', equals: 'gasMeter' },
          },
          // RESIDENTIAL ELEVATOR: the top stop (a level — blank = the next level up)
          // and which stop the cab is shown parked at
          {
            key: 'elevatorTopLevelId', label: 'Top Stop', kind: 'level', default: '',
            conditionalOn: { param: 'objectKind', equals: 'elevator' },
          },
          {
            key: 'elevatorCabAt', label: 'Cab Shown At', kind: 'choice', default: 'bottom',
            choices: [{ value: 'bottom', label: 'Bottom stop' }, { value: 'top', label: 'Top stop' }],
            conditionalOn: { param: 'objectKind', equals: 'elevator' },
          },
          // RESIDENTIAL ELEVATOR WIRING (2026-10-06): the drive decides where the
          // controller sits and so which services land where (hookups.ts
          // ELEVATOR_DRIVES — keep the two lists in step); the controller side is
          // the hoistway wall that carries them, as seen from the landing facing
          // the door; the call height is each hall call station's centreline
          {
            key: 'elevatorDrive', label: 'Drive', kind: 'choice', default: 'hydraulic',
            choices: [
              { value: 'hydraulic', label: 'Hydraulic — machine room' },
              { value: 'machineRoomLess', label: 'Cable / chain drive — no machine room' },
              { value: 'pneumatic', label: 'Pneumatic vacuum (no pit)' },
              { value: 'throughFloor', label: 'Through-floor lift (no shaft)' },
            ],
            conditionalOn: { param: 'objectKind', equals: 'elevator' },
          },
          {
            key: 'elevatorControllerSide', label: 'Controller Side', kind: 'choice', default: 'left',
            choices: [{ value: 'left', label: 'Left (facing the door)' }, { value: 'right', label: 'Right (facing the door)' }],
            conditionalOn: { param: 'objectKind', equals: 'elevator' },
          },
          {
            key: 'elevatorCallHeightFt', label: 'Call Station Height', kind: 'length', default: 3.5, min: 2.5, max: 4.5,
            conditionalOn: { param: 'objectKind', equals: 'elevator' },
          },
          {
            // COLUMN FRIDGE HINGE: which side the panel door hinges on (facing the front)
            key: 'fridgeHinge', label: 'Door Hinge', kind: 'choice', default: 'left',
            choices: [{ value: 'left', label: 'Left' }, { value: 'right', label: 'Right' }],
            conditionalOn: { param: 'objectKind', equals: 'columnFridge' },
          },
          {
            // SHOWER DOOR FLIP (2026-10-01, Matt): which end the glass door hinges on,
            // looking from its front, and which side it swings to — the Flip hinge /
            // Flip swing ribbon buttons toggle these
            key: 'showerDoorHinge', label: 'Hinge Side', kind: 'choice', default: 'left',
            choices: [{ value: 'left', label: 'Left' }, { value: 'right', label: 'Right' }],
            conditionalOn: { param: 'objectKind', equals: 'showerDoor' },
          },
          {
            key: 'showerDoorSwing', label: 'Swing Side', kind: 'choice', default: 'out',
            choices: [{ value: 'out', label: 'Out (toward the front)' }, { value: 'in', label: 'In (toward the back)' }],
            conditionalOn: { param: 'objectKind', equals: 'showerDoor' },
          },
          {
            // FURNITURE: pick a standard mattress — the frame is sized around it
            key: 'bedSize', label: 'Mattress Size', kind: 'choice', default: 'queen',
            choices: BED_SIZES.map((b) => ({ value: b.value, label: b.label })),
            conditionalOn: { param: 'objectKind', equals: 'bed' },
          },
          {
            key: 'frameMarginIn', label: 'Frame Beyond Mattress (in)', kind: 'number', default: BED_FRAME_MARGIN_IN, min: 0, max: 12,
            conditionalOn: { param: 'objectKind', equals: 'bed' },
          },
          {
            key: 'headboardIn', label: 'Headboard Depth (in)', kind: 'number', default: BED_HEADBOARD_IN, min: 0, max: 12,
            conditionalOn: { param: 'objectKind', equals: 'bed' },
          },
        ],
      },
      {
        // COOKTOPS (2026-10-15): the look is per PLACEMENT (one generic Cooktop type —
        // nobody needs type-edit rights to put an induction top in): its style, its burner /
        // zone count (0 = the usual count for its width) and a pro rangetop's griddle or
        // charbroiler. applyObjectInstanceSize folds these into the type params the plan
        // symbol, the 3D body and the hookups read.
        label: 'Cooktop',
        params: [
          {
            key: 'cooktopStyle', label: 'Style', kind: 'choice', default: 'gas',
            choices: COOKTOP_STYLES.map((s) => ({ value: s.value, label: s.label })),
            conditionalOn: { param: 'objectKind', equals: 'cooktop' },
          },
          {
            key: 'cooktopBurners', label: 'Burners / Zones (0 = usual for its width)', kind: 'number', default: 0, min: 0, max: 8,
            conditionalOn: { param: 'objectKind', equals: 'cooktop' },
          },
          {
            key: 'cooktopZone', label: 'Griddle / Charbroiler', kind: 'choice', default: 'none',
            choices: RANGE_COOKTOP_EXTRAS.map((c) => ({ value: c.value, label: c.label })),
            conditionalOn: { param: 'objectKind', equals: 'cooktop' },
          },
        ],
      },
      {
        label: 'Shower Pan',

        params: [
          {
            key: 'panWidthFt', label: 'Pan Width', kind: 'length', default: 0, min: 0, max: 12,
            conditionalOn: { param: 'objectKind', equals: 'shower' },
          },
          {
            key: 'panDepthFt', label: 'Pan Depth', kind: 'length', default: 0, min: 0, max: 12,
            conditionalOn: { param: 'objectKind', equals: 'shower' },
          },
        ],
      },
    ],
  },
}
